import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import {
  StructureGridCollectionsQueryResponse,
  StructureGridQueryResponse,
} from 'types/api-responses';
import { gridSurfaceMapAtom } from './atoms';
import { isNil } from 'lodash-es';
import { AtomField } from 'atoms/form-atoms';

interface StructureGridSearchParams extends SearchParams {
  basinName: string;
  geologicInterval: string;
  status: string;
}

interface StructureGridCollectionsQuerySearchParams extends SearchParams {
  basin?: string;
  owner?: string;
  created_at?: {
    start?: string;
    end?: string;
  };
  sort_by?: string;
  sort_order?: string;
}

export const useGridSurfaces = (
  selectedGeologicIntervalId: number | null,
  geologicInterval: string,
  basinField: AtomField<string>
) => {
  const basin = useAtomValue(basinField);

  const { searchAtom: structureGridSearchAtom } = useMemo(
    () =>
      searchAtomPair<StructureGridQueryResponse, StructureGridSearchParams>(
        'structureGrids'
      ),
    []
  );

  const [structureGridSearchResult, setStructureGridSearchResult] = useAtom(
    structureGridSearchAtom
  );

  const [gridSurfaceMap, setGridSurfaceMap] = useAtom(gridSurfaceMapAtom);

  const gridSurfaces = !isNil(selectedGeologicIntervalId)
    ? gridSurfaceMap.get(selectedGeologicIntervalId)
    : null;

  useEffect(() => {
    if (!isNil(selectedGeologicIntervalId) && !gridSurfaces) {
      setStructureGridSearchResult((val) => {
        return {
          ...val,
          basinName: basin.value,
          geologicInterval: geologicInterval,
          status: 'processed',
          paginated: false,
        };
      });
    }
  }, [
    selectedGeologicIntervalId,
    basin.value,
    geologicInterval,
    setStructureGridSearchResult,
    gridSurfaces,
  ]);

  useEffect(
    () => {
      if (
        structureGridSearchResult?.data?.results &&
        selectedGeologicIntervalId !== null
      ) {
        setGridSurfaceMap((map) => {
          return new Map(map).set(
            selectedGeologicIntervalId,
            structureGridSearchResult?.data?.results || []
          );
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structureGridSearchResult?.data?.results, setGridSurfaceMap]
  );

  return {
    loading: structureGridSearchResult?.loading,
    error: structureGridSearchResult?.error,
    gridSurfaces,
  };
};

export const useFilterOptions = () => {
  return useMemo(() => {
    const { searchAtom } = searchAtomPair<
      StructureGridCollectionsQueryResponse,
      StructureGridCollectionsQuerySearchParams
    >('structureGridCollections', {
      paginated: false,
    });

    return atom((get) => {
      const searchResults = get(searchAtom);
      return {
        basins: Array.from(
          new Set(
            searchResults.data?.results?.map((result) => result.basin) || []
          )
        ).sort(),
        owners: Array.from(
          new Set(
            searchResults.data?.results?.map((result) => result.owner) || []
          )
        ).sort(),
        loading: searchResults.loading,
        error: searchResults.error,
      };
    });
  }, []);
};
